/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import QueryString from 'query-string';
import { selectedTeamFlatContributorsSelector } from 'state/selectors/contributors';
import { getContributorAvatarUrl } from 'common/constants/Avatars';
import GridChipFilterContainer from 'components/GridChipFilterContainer/GridChipFilterContainer';
import debounce from 'debounce-promise';

import {
  convertSearchToRequest,
  handleSearchParams,
  trackSearch,
  AUTOCOMPLETE_TYPES
} from 'components/GridChipFilterContainer/helpers';
import { createTeamIdFilterConfig } from 'components/GridChipFilterContainer/common-filters';
import {
  getPullRequests,
  getPullRequestsSearchSummary
} from 'common/services/pull-requests.service';
import * as TrackingService from 'common/services/tracking.service';
import { getColumns, filterChipLabelMap, getPrsTableData } from './pr.service';
import {
  ARTICLE_IDS,
  TRACKING,
  SEARCH_DATE_FORMAT,
  SEARCH_TIMEDATE_FORMAT,
  DAYS_PARAM,
  SETTING_NAMES
} from 'common/constants/constants';
import {
  getMinDateForDatePicker,
  navigateBookDemoClick
} from 'common/utils/utils';
import { getDefaultFormatter } from 'common/utils/time.utils';
import { getDurationLevels } from '@linearb/linui-utils';
import { SettingValues } from 'common/services/settings.service';
import { useSelector } from 'react-redux';
import { getServicesSelector } from 'state/selectors/services';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import { DefinitionLevels, FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import { useHistory, useLocation } from 'react-router';
import { usePullRequestLabels } from 'common/fetchHooks/usePullRequestLabels';
import CustomFilterWithCTA from '../../../../../../../../components/customFilter/CustomFilterWithCTA';
import { COMPONENT_TYPE } from 'components/GridChipFilterContainer/consts';
import {
  CTA_TOOLTIP_TEXTS,
  CUSTOM_COMPONENTS_PLACEHOLDERS,
  DORA_DASHBOARD_V2_EVENT_DATES_LIMIT_DAYS,
  TRACKING as TRACKING_EVENTS
} from '../../../dashboard/doraV2/dashboard.doraV2.freeTier.consts';
import useDoraFeatureFlags from 'views/root/views/home/hooks/useDoraFeatureFlags';
import { useDefaultDateRange } from '../../useDefaultDateRange';
import useTeamGitFilters from '../../hooks/useTeamGitFilters';
import { repositoriesSelector } from 'state/selectors/repositories';

const updatedInLabel = `Updated`;
const mergedInLabel = `Merged`;
const superficialReview = `Superficial Review`;
const longLivingLabel = `Long Living`;
const highInteractionLabel = `High Interaction`;
const mergedWithoutReview = `Merged w/o Review`;
const longWaitingForReview = `Review Request Hanging`;
const currentPeriod = `Current Period`;

// eslint-disable-next-line max-lines-per-function
const PullRequests = ({
  contributorsById,
  currentTeams = [],

  prs,
  getPullRequestsSingle,
  latestIteration,
  selectedTeam,
  isTeamLicense,
  myAccount,
  license
}) => {
  const location = useLocation();
  const history = useHistory();
  const contributors = useSelector(selectedTeamFlatContributorsSelector) || [];
  const repositories = useSelector(repositoriesSelector);
  const { pullRequestLabels } = usePullRequestLabels();
  const services = useSelector(getServicesSelector);
  const isSelectedTeamParent = useSelector(isSelectedTeamParentSelector);
  const { getSettingOrDefinitionValue } = useSettingsService();

  const servicesTabEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.servicesTabEnabled
  });
  const prMetricsPerBranchContributorsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.prMetricsPerBranchContributorsEnabled
  });
  const { enableTeamSelection, isAllControbutorsTeamSelected } =
    useDoraFeatureFlags();
  const [pullRequestsTotals, setPullRequestsTotals] = useState({});
  const showTeamIdFilters =
    (!selectedTeam || isSelectedTeamParent) && !isTeamLicense;

  const teamId = _.get(selectedTeam, 'id', null);
  const codeChangesSmallTopRange = getSettingOrDefinitionValue(
    { name: SettingValues.CODE_CHANGES_SMALL_TOP_RANGE },
    0
  );
  const codeChangesMediumTopRange = getSettingOrDefinitionValue(
    { name: SettingValues.CODE_CHANGES_MEDIUM_TOP_RANGE },
    0
  );
  const { from, to } = useDefaultDateRange();
  const showPersonalInformation = getSettingOrDefinitionValue(
    { name: SETTING_NAMES.SHOW_PERSONAL_INFORMATION },
    false
  );
  const longWaitingHours = getSettingOrDefinitionValue(
    {
      name: SettingValues.LONG_LIVING_THRESHOLDS,
      key: 'review_time',
      definitionLevel: DefinitionLevels.TEAM,
      teamId
    },
    48
  );
  const superficialReviewTime = getSettingOrDefinitionValue(
    {
      name: SettingValues.SUPERFICIAL_REVIEW_THRESHOLDS,
      key: 'review_time',
      definitionLevel: DefinitionLevels.TEAM,
      teamId
    },
    20
  );
  const superficialReviewSize = getSettingOrDefinitionValue(
    {
      name: SettingValues.SUPERFICIAL_REVIEW_THRESHOLDS,
      key: 'pr_size',
      definitionLevel: DefinitionLevels.TEAM,
      teamId
    },
    20
  );
  const mergedWithoutReviewPrSize = getSettingOrDefinitionValue(
    {
      name: SettingValues.MERGED_WITHOUT_REVIEW_THRESHOLDS,
      key: 'pr_size',
      definitionLevel: DefinitionLevels.TEAM,
      teamId
    },
    20
  );
  const getSelectedTeamParams = () =>
    selectedTeam ? { filtered_teams: selectedTeam.id.toString() } : {};
  const TABLE_SKELETONS = [
    'avatars-random',
    'singleline-random',
    'singleline-random',
    'singleline',
    'singleline-random',
    'singleline-random',
    'singleline-random',
    'singleline',
    'singleline-random'
  ];
  const filtersConfig = {
    [updatedInLabel]: {
      articleId: ARTICLE_IDS.UPDATED_IN_ITERATION,
      subTitle: `in ${latestIteration.name}`,
      amplitudeClickType: TRACKING.CLICK.PREDEFINED_UPDATED,
      searchState: {
        order_by: `updated_at,desc`,
        updated: `${latestIteration.start_date},${latestIteration.end_date}`,
        ...getSelectedTeamParams()
      }
    },
    [currentPeriod]: {
      searchState: {
        order_by: `updated_at,desc`,
        updated: `${from},${to}`,
        ...getSelectedTeamParams()
      }
    },
    [mergedInLabel]: {
      articleId: ARTICLE_IDS.MERGED_IN_ITERATION,
      subTitle: `in ${latestIteration.name}`,
      amplitudeClickType: TRACKING.CLICK.PREDEFINED_MERGED,
      searchState: {
        order_by: `updated_at,desc`,
        merged: `${latestIteration.start_date},${latestIteration.end_date}`,
        ...getSelectedTeamParams()
      }
    },
    [superficialReview]: {
      articleId: ARTICLE_IDS.LIGHTNING_PR,
      subTitle: `in ${latestIteration.name}`,
      amplitudeClickType: TRACKING.CLICK.PREDEFINED_LIGHTNING_PRS,
      searchState: {
        order_by: `review_time,asc`,
        'PR State': 'merged',
        'Review time': `0-${superficialReviewTime}`,
        'PR size': `>${superficialReviewSize}`,
        updated: `${latestIteration.start_date},${latestIteration.end_date}`,
        ...getSelectedTeamParams()
      }
    },
    [longLivingLabel]: {
      articleId: ARTICLE_IDS.LONG_LIVING_PR,
      subTitle: 'Open for more than 30 days',
      amplitudeClickType: TRACKING.CLICK.PREDEFINED_LONG_LIVING,
      searchState: {
        order_by: `updated_at,asc`,
        published: `,${moment()
          .subtract(30, DAYS_PARAM)
          .format(SEARCH_DATE_FORMAT)}`,
        'PR State': 'initiated,in-review',
        ...getSelectedTeamParams()
      }
    },
    [highInteractionLabel]: {
      articleId: ARTICLE_IDS.HIGH_INT_PR,
      subTitle: `in ${latestIteration.name}`,
      amplitudeClickType: TRACKING.CLICK.PREDEFINED_HIGH_INTERACTION,
      searchState: {
        order_by: `comment_count,desc`,
        updated: `${latestIteration.start_date},${latestIteration.end_date}`,
        comment_count: '>10',
        draft: '0',
        ...getSelectedTeamParams()
      }
    },
    [mergedWithoutReview]: {
      articleId: ARTICLE_IDS.MERGED_WITHOUT_REVIEW,
      subTitle: `Merged without review in ${latestIteration.name}`,
      amplitudeClickType: TRACKING.CLICK.PREDEFINED_MERGED_WO_REVIEW,
      searchState: {
        order_by: `updated_at,desc`,
        merged: `${latestIteration.start_date},${latestIteration.end_date}`,
        'PR size': `>${mergedWithoutReviewPrSize}`,
        Reviewed: '1',
        ...getSelectedTeamParams()
      }
    },
    [longWaitingForReview]: {
      subTitle: `Waiting for more than ${getDurationLevels({
        minutes: longWaitingHours * 60,
        format: getDefaultFormatter({
          minutes: longWaitingHours * 60,
          daysUnit: ' days',
          hoursUnit: ' hours',
          yearsUnit: ' years'
        }),
        joiner: ' ',
        levels: 1
      })}`,
      amplitudeClickType: TRACKING.CLICK.PREDEFINED_REVIEW_REQUEST_HANGING,
      articleId: ARTICLE_IDS.REVIEW_REQUEST_HANGING,
      searchState: {
        date_before_key: 'before_timestamp',
        order_by: `updated_at,desc`,
        published: `,${moment()
          .subtract(longWaitingHours, 'hours')
          .format(SEARCH_TIMEDATE_FORMAT)}`,
        'PR State': 'initiated',
        date_format: SEARCH_TIMEDATE_FORMAT,
        ...getSelectedTeamParams()
      }
    },
    ...['updated', 'created', 'merged', 'published'].reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          type: 'date',
          reqKey: `${key}_at`,
          maxDate: () => moment().format(SEARCH_DATE_FORMAT),
          minDate: () =>
            isAllControbutorsTeamSelected
              ? moment().subtract(
                  DORA_DASHBOARD_V2_EVENT_DATES_LIMIT_DAYS,
                  'days'
                )
              : getMinDateForDatePicker(license)
        }
      }),
      {}
    ),
    order_by: {
      type: 'order_by',
      reqKey: 'order_by'
    },
    'PR State': {
      type: 'multi-checkbox',
      options: [
        'initiated',
        'in-review',
        'merged',
        'closed',
        { key: 'draft' }
      ].filter(Boolean),
      searchValueTransformFn: query => {
        const payload = {};
        if (query.split(',').some(s => s.toLowerCase() === 'draft')) {
          payload.is_draft = true;
        }
        // this is a temp handling because the backend doesnt support empty arrays as expected
        const otherStates = query
          .split(',')
          .filter(s => s.toLowerCase() !== 'draft');

        if (otherStates.length) {
          payload.state = otherStates;
        }
        return payload;
      }
    },
    [prMetricsPerBranchContributorsEnabled ? 'contributors' : 'authors']: {
      type: 'multi-autocomplete',
      reqKey: 'contributor_ids',
      options: contributors,
      getAvatar: ({ name, extended_data, id }) => ({
        url: getContributorAvatarUrl({ id, extended_data }),
        name
      }),
      removeFromSearchParamsFn: ({ values, options }) =>
        _.intersection(options, values)
    },
    filtered_teams: createTeamIdFilterConfig(
      'multi-tree-dropdown',
      currentTeams,
      history,
      location
    ),
    repositories: {
      type: 'multi-autocomplete',
      reqKey: 'repository_ids',
      options: repositories
    },
    services: {
      type: 'multi-autocomplete',
      reqKey: 'service_ids',
      options: services
    },
    Reviewed: {
      type: 'radio',
      reqKey: 'unreviewed',
      searchValueTransformFn: value => value === '1',
      options: [
        { value: '0', label: 'Reviewed' },
        { value: '1', label: 'Unreviewed' }
      ]
    },
    comment_count: {
      type: 'radio',
      reqKey: 'comment_count',
      options: [
        { value: '0-0', label: 'None - 0' },
        { value: '1-5', label: 'Small - 1-5' },
        { value: '5-10', label: 'Medium - 5-10' },
        { value: '>10', label: 'Large - 10+' }
      ]
    },
    'Review time': {
      type: 'radio',
      reqKey: 'review_time',
      options: [
        { value: '0-20', label: '0-20m' },
        { value: '20-120', label: '20m-2h' },
        { value: '120-360', label: '2h-6h' },
        { value: '>360', label: '6h+' }
      ]
    },
    used_in_metrics_calculation: {
      type: 'radio',
      reqKey: 'cycle_time_excluded_branches',
      searchValueTransformFn: value => value === 'true',
      options: [{ value: 'true', label: 'True' }]
    },
    'PR size': {
      type: 'radio',
      reqKey: 'pr_size',
      options: [
        {
          value: `0-${codeChangesSmallTopRange}`,
          label: `Small 0-${codeChangesSmallTopRange}`
        },
        {
          value: `${codeChangesSmallTopRange}-${codeChangesMediumTopRange}`,
          label: `Medium ${codeChangesSmallTopRange}-${codeChangesMediumTopRange}`
        },
        {
          value: `>${codeChangesMediumTopRange}`,
          label: `Large - ${codeChangesMediumTopRange}+`
        }
      ]
    },
    draft: {
      type: 'query_only',
      reqKey: 'is_draft',
      searchValueTransformFn: value =>
        _.isBoolean(value) ? value : value === '1'
    },
    labels: {
      type: 'multi-autocomplete',
      reqKey: 'labels',
      options: pullRequestLabels.map(label => ({
        name: label,
        id: label
      }))
    }
  };

  const [predefinedFilterStats, setPredefinedFilterStats] = useState({});

  const fetchPullRequests = async params => {
    setPullRequestsTotals({});
    getPullRequests({
      ...params,
      fetch_summary_only: true,
      avoid_fetching_teams: true,
      fetch_contributor_columns: false
    })
      .then(response => {
        setPullRequestsTotals(response);
      })
      .catch(err => {
        setPullRequestsTotals({});
      });
    return await getPullRequestsSingle({
      ...params,
      fetch_contributor_columns: false
    });
  };
  const memoizedGetPullRequests = useCallback(
    debounce(fetchPullRequests, 500),
    []
  );

  const getPredefinedFilterStats = async () => {
    /** Get all the names of the predefined filters */
    const predefinedFilterNames = _.get(
      filtersGroupMap.find(({ type }) => type === 'predefined'),
      'names',
      []
    ).filter(name => !!_.get(filtersConfig, `${name}.searchState`, null));

    /** Map each predefined filter name to its request */
    const predefinedFilterRequests = predefinedFilterNames.map(name =>
      convertSearchToRequest(
        _.get(filtersConfig, `${name}.searchState`),
        filtersConfig
      )
    );
    /** Fetch all the predefined filters stats and map them by name */
    try {
      const stats = await getPullRequestsSearchSummary(
        predefinedFilterRequests
      );
      const predefinedFiltersStatsMappedByLabel = predefinedFilterNames.reduce(
        (acc, name, idx) => ({ ...acc, [name]: stats[idx] }),
        {}
      );
      setPredefinedFilterStats(predefinedFiltersStatsMappedByLabel);
    } catch (e) {
      setPredefinedFilterStats({});
    }
  };

  const getReqFilterBody = () => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });

    return {
      ...convertSearchToRequest(search, filtersConfig),
      teams: [selectedTeam?.id]
    };
  };
  useEffect(() => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });
    if (Object.keys(search).length === 0) {
      const newSearchString = `?${QueryString.stringify(
        Object.assign({}, {}, filtersConfig[currentPeriod].searchState)
      )}`;
      history.replace({
        pathname: location.pathname,
        search: newSearchString
      });
    }
  }, []);

  useEffect(() => {
    const search = QueryString.parse(location.search, {
      ignoreQueryPrefix: true
    });

    trackSearch(
      TRACKING.EVENTS.FILTER,
      search,
      ['repositories', 'contributors'],
      TRACKING.PAGES.ACTIVITY,
      TRACKING.PAGE_TABS.PRS
    );
  }, [location.search]);

  useTeamGitFilters();

  const navigateAndTrackBookDemo = () => {
    TrackingService.logEvent(TRACKING.EVENTS.CTA_CLICK, {
      page: TRACKING.PAGES.ACTIVITY,
      tab: TRACKING.PAGE_TABS.PRS,
      cta: TRACKING_EVENTS.CTA_TYPE.FILTERS,
      intent: TRACKING.CTA_INTENT.BOOK_A_DEMO,
      source_url: window.location.href
    });
    navigateBookDemoClick();
  };

  const filtersGroupMap = [
    {
      type: 'predefined',
      title: 'PREDEFINED FILTERS',
      page: 'PRs',
      itemsWeight: '500',
      defaultIsOpen: true,
      names: [
        updatedInLabel,
        mergedInLabel,
        superficialReview,
        highInteractionLabel,
        longLivingLabel,
        longWaitingForReview,
        mergedWithoutReview
      ]
    },
    {
      type: !enableTeamSelection
        ? COMPONENT_TYPE.CUSTOM_FILTER
        : COMPONENT_TYPE.AUTOCOMPLETE_BY_TYPE,

      items: [
        ...(showPersonalInformation
          ? [
              {
                type: AUTOCOMPLETE_TYPES.AUTOCOMPLETE,
                name: prMetricsPerBranchContributorsEnabled
                  ? 'contributors'
                  : 'authors',
                placeholderName: 'authors',
                addAvatars: true
              }
            ]
          : []),
        ...(showTeamIdFilters
          ? [
              {
                type: AUTOCOMPLETE_TYPES.TREE_TEAMS_DROPDOWN,
                name: 'filtered_teams',
                addAvatars: true,
                multiple: true
              }
            ]
          : [])
      ],
      title: showTeamIdFilters
        ? prMetricsPerBranchContributorsEnabled
          ? `Contributors / Teams`
          : `Owners / Teams`
        : prMetricsPerBranchContributorsEnabled
        ? 'Contributors'
        : 'Authors',
      defaultIsOpen: true,
      CustomFilter: (
        <>
          <CustomFilterWithCTA
            type={COMPONENT_TYPE.AUTOCOMPLETE}
            ctaButtonText={CTA_TOOLTIP_TEXTS.BOOK_A_DEMO}
            ctaMessage={CTA_TOOLTIP_TEXTS.ACTIVITY_AUTHORS}
            customComponentProps={{
              placeholder: prMetricsPerBranchContributorsEnabled
                ? CUSTOM_COMPONENTS_PLACEHOLDERS.ALL_CONTRIBUTORS
                : CUSTOM_COMPONENTS_PLACEHOLDERS.ALL_AUTHOR
            }}
            navigateBookDemoClick={navigateAndTrackBookDemo}
          />
          {showTeamIdFilters && (
            <CustomFilterWithCTA
              type={COMPONENT_TYPE.AUTOCOMPLETE}
              ctaButtonText={CTA_TOOLTIP_TEXTS.BOOK_A_DEMO}
              ctaMessage={CTA_TOOLTIP_TEXTS.ACTIVITY_TEAMS}
              customComponentProps={{
                placeholder: CUSTOM_COMPONENTS_PLACEHOLDERS.ALL_TEAMS
              }}
              navigateBookDemoClick={navigateAndTrackBookDemo}
            />
          )}
        </>
      )
    },
    ...(servicesTabEnabledFF
      ? [
          {
            type: !enableTeamSelection
              ? COMPONENT_TYPE.CUSTOM_FILTER
              : COMPONENT_TYPE.AUTOCOMPLETE,
            items: [
              { name: 'services', defaultIsOpen: false, addAvatars: false }
            ],
            title: 'Services',
            defaultIsOpen: true,
            CustomFilter: (
              <CustomFilterWithCTA
                type={COMPONENT_TYPE.AUTOCOMPLETE}
                ctaButtonText={CTA_TOOLTIP_TEXTS.BOOK_A_DEMO}
                ctaMessage={CTA_TOOLTIP_TEXTS.ACTIVITY_SERVICES}
                customComponentProps={{
                  placeholder: CUSTOM_COMPONENTS_PLACEHOLDERS.ALL_SERVICES
                }}
                navigateBookDemoClick={navigateAndTrackBookDemo}
              />
            )
          }
        ]
      : []),
    {
      type: 'autocomplete',
      items: [
        { name: 'repositories', defaultIsOpen: false, addAvatars: false }
      ],
      title: 'Repositories',
      defaultIsOpen: true
    },
    {
      type: 'autocomplete',
      items: [
        {
          name: 'labels',
          title: 'PR labels',
          defaultIsOpen: false,
          addAvatars: false
        }
      ],
      title: 'PR Labels',
      defaultIsOpen: true
    }
  ].filter(filterGroup =>
    filterGroup.items ? filterGroup.items.length : true
  );

  const additionalFilterGroupMap = [
    {
      type: 'checkbox',
      categories: [{ name: 'PR State', articleId: ARTICLE_IDS.PR_STATE }],
      defaultIsOpen: true,
      title: 'PR State'
    },
    {
      type: 'date',
      names: ['created', 'updated', 'merged'],
      defaultIsOpen: false,
      title: 'Event Dates'
    },
    {
      type: 'radio',
      names: [{ name: 'comment_count', label: '' }],
      defaultIsOpen: false,
      title: 'Comments'
    },
    {
      type: 'radio',
      names: [{ name: 'Review time', label: '' }],
      defaultIsOpen: false,
      title: 'Review time'
    },
    {
      type: 'radio',
      names: [{ name: 'Reviewed', label: '' }],
      defaultIsOpen: false,
      title: 'Reviewed'
    },
    {
      type: 'radio',
      names: [{ name: 'PR size', label: '' }],
      defaultIsOpen: false,
      title: 'PR Size'
    },
    {
      type: 'radio',
      names: [{ name: 'used_in_metrics_calculation', label: '' }],
      defaultIsOpen: false,
      title: 'Used in metrics calculation'
    }
  ];
  useEffect(() => {
    const searchParams = handleSearchParams({ filtersConfig });

    history.replace({
      pathname: location.pathname,
      search: `?${QueryString.stringify(searchParams)}`
    });

    _.debounce(getPredefinedFilterStats, 500)();
  }, [
    selectedTeam?.id,
    latestIteration?.start_date,
    latestIteration?.end_date
  ]);

  useEffect(() => {
    const search = QueryString.parse(window.location.search, {
      ignoreQueryPrefix: true
    });
    const { event_source, event_noti_id } = search;

    history.replace({
      pathname: location.pathname,
      search: `?${QueryString.stringify(
        Object.assign({}, {}, _.omit(search, ['event_source', 'event_noti_id']))
      )}`
    });

    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.ACTIVITY,
      page_tab: TRACKING.PAGE_TABS.PRS,
      ...(event_source ? { event_source } : {}),
      ...(event_noti_id ? { event_noti_id } : {})
    });
  }, []);

  const filterConfigWithBadges = {
    ...filtersConfig,
    ...Object.keys(predefinedFilterStats).reduce(
      (acc, name) => ({
        ...acc,
        [name]: {
          ...filtersConfig[name],
          badge: predefinedFilterStats[name].count
        }
      }),
      {}
    )
  };

  return (
    <GridChipFilterContainer
      disablePointInHistory
      getData={params => memoizedGetPullRequests(params)}
      teamsFilterKey='filtered_teams'
      data={{ ...prs, summary: pullRequestsTotals }}
      tableData={({ onContRepoTableCellClick }) =>
        prs.items.length
          ? getPrsTableData({
              contributorsById,
              contributors,
              data: prs.items,
              onRepositoryClick: onContRepoTableCellClick(
                'repositories',
                repositories,
                item => item
              ),
              onContributorClick: onContRepoTableCellClick(
                'author',
                contributors,
                ({ name }) => name
              ),
              myAccount,
              showPersonalInformation,
              prMetricsPerBranchContributorsEnabled
            })
          : []
      }
      // skip pageTag since we're logging the visit in useEffect above
      //   pageTag={TRACKING.PAGES.ACTIVITY}
      //   pageTabTag={TRACKING.PAGE_TABS.PRS}
      filterChipLabelMap={filterChipLabelMap}
      columns={getColumns(
        { ...prs, summary: pullRequestsTotals },
        prMetricsPerBranchContributorsEnabled
      )}
      skeletonColumns={TABLE_SKELETONS}
      getReqFilterBody={getReqFilterBody}
      filtersConfig={filterConfigWithBadges}
      filtersGroupMap={filtersGroupMap}
      additionalFiltersGroupMap={additionalFilterGroupMap}
      refreshOnDependencies={[currentTeams, selectedTeam, pullRequestLabels]}
    />
  );
};

export default PullRequests;
